import React, { useEffect, useState, useCallback } from 'react';
import EventTraceabilityStatus from '../../components/traceability/EventTraceabilityStatus';
import { evaluationPercentage } from '../../lib/evaluationIntegrity';
import { buildSummary } from '../../lib/evaluationSummary';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, clientsAPI, projectMetaAPI } from '../../api/client';
import { templatesAPI } from '../../api/templates';
import { isTemplateSelectable } from '../../lib/templateStatus';
import { useAuth } from '../../context/AuthContext';
import SemaforoBadge from '../../components/SemaforoBadge';
import { VersionTrendChart } from '../../components/ScoreChart';
import CreateProjectEventModal from '../../components/CreateProjectEventModal';
import { EVENT_TYPES, EVENT_STATUS, RISK_LEVEL, getDaysLabel } from '../../lib/eventConstants';
import { DictamenCard } from '../../lib/dictamen';
import {
  ArrowLeft,
  Plus,
  Calendar,
  ClipboardList,
  ArrowRight,
  AlertTriangle,
  GitCompare,
  TrendingUp,
  TrendingDown,
  BarChart2,
  CheckCircle,
  Clock,
  X,
  Settings,
  LayoutGrid,
  List,
  Activity,
  Edit2,
  Link,
} from 'lucide-react';
import { toast } from 'sonner';


// ─── Allowed Templates Manager ────────────────────────────────────────────────
function AllowedTemplatesModal({ projectId, allTemplates, current, onSave, onClose }) {
  const [selected, setSelected] = useState(new Set(current));
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await projectMetaAPI.setAllowedTemplates(projectId, Array.from(selected));
      onSave(Array.from(selected));
      toast.success('Plantillas permitidas actualizadas');
      onClose();
    } catch { toast.error('Error al actualizar plantillas'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy">
          <h3 className="font-bold text-white">Plantillas permitidas</h3>
          <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-slate-500 text-sm">
            Selecciona qué plantillas pueden usarse en este proyecto.
            Si no seleccionas ninguna, se permiten todas las plantillas activas.
          </p>
          {/* #Fase0-B3 — antes filtraba status === 'active', que el backend
              nunca devuelve ('activa'): la lista salía siempre vacía. Solo
              se ofrecen activas (ni borradores, ni retiradas, ni archivadas). */}
          {allTemplates.filter(isTemplateSelectable).map(t => (
            <label key={t.id} className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer ${selected.has(t.id) ? 'border-soem-primary bg-blue-50' : 'border-slate-200'}`}>
              <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggle(t.id)} />
              <div>
                <p className="font-semibold text-soem-navy text-sm">{t.name}</p>
                <p className="text-slate-400 text-xs">{t.sections_count ?? t.section_count ?? 0} secciones</p>
              </div>
            </label>
          ))}
          {allTemplates.filter(isTemplateSelectable).length === 0 && (
            <p className="text-slate-400 text-sm text-center py-4">No hay plantillas activas.</p>
          )}
          <div className="flex gap-3 pt-2">
            <button onClick={handleSave} disabled={saving} className="soem-btn-primary flex-1 py-2 text-sm disabled:opacity-60">
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
            <button onClick={onClose} className="soem-btn-secondary flex-1 py-2 text-sm">Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Project Modal ───────────────────────────────────────────────────────
function EditProjectModal({ project, onSave, onClose }) {
  const [form, setForm] = useState({
    name: project?.name || '',
    description: project?.description || '',
    deadline: project?.deadline?.slice(0, 10) || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.deadline) delete payload.deadline;
      await onSave(payload);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al actualizar proyecto');
    } finally { setSaving(false); }
  };

  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy">
          <h3 className="font-bold text-white">Editar Proyecto</h3>
          <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label htmlFor="f-nombre-del-proyecto" className="soem-label block mb-1">Nombre del proyecto</label>
            <input id="f-nombre-del-proyecto" className={inputCls} value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="edit-project-name" />
          </div>
          <div>
            <label htmlFor="f-descripción" className="soem-label block mb-1">Descripción</label>
            <textarea id="f-descripción" className={`${inputCls} resize-none`} rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="edit-project-description" />
          </div>
          <div>
            <label htmlFor="f-fecha-límite-opcional" className="soem-label block mb-1">Fecha límite (opcional)</label>
            <input id="f-fecha-límite-opcional" type="date" className={inputCls} value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} data-testid="edit-project-deadline" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="soem-btn-primary flex-1 py-2 text-sm disabled:opacity-60" data-testid="edit-project-save">
              {saving ? 'Guardando...' : 'Actualizar Proyecto'}
            </button>
            <button type="button" onClick={onClose} className="soem-btn-secondary flex-1 py-2 text-sm">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Link Event Modal ─────────────────────────────────────────────────────────
function LinkEventModal({ projectId, existingEventIds, onLinked, onClose }) {
  const [search, setSearch] = useState('');
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { eventsAPI } = await import('../../api/client');
        const r = await eventsAPI.list();
        // Filter out events already linked to this project
        const available = (r.data.items || []).filter(e => !existingEventIds.includes(e.id));
        setAllEvents(available);
      } catch { toast.error('Error al cargar eventos'); }
      finally { setLoading(false); }
    })();
  }, [existingEventIds]);

  const filtered = search
    ? allEvents.filter(e => e.name?.toLowerCase().includes(search.toLowerCase()) || e.event_code?.toLowerCase().includes(search.toLowerCase()))
    : allEvents;

  const handleLink = async (eventId) => {
    setLinking(true);
    try {
      await projectsAPI.linkEvent(projectId, eventId);
      toast.success('Evento vinculado al proyecto');
      onLinked();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al vincular evento');
    } finally { setLinking(false); }
  };

  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy">
          <h3 className="font-bold text-white">Vincular Evento Existente</h3>
          <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>
        <div className="p-4 border-b">
          <input className={inputCls} placeholder="Buscar por nombre o código..."
            value={search} onChange={e => setSearch(e.target.value)} autoFocus />
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-slate-100 rounded-sm animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8">
              <Calendar size={36} className="text-slate-200 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">No hay eventos disponibles para vincular.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(evt => {
                const et = EVENT_TYPES[evt.event_type] || { emoji: '📅', label: evt.event_type };
                return (
                  <div key={evt.id} className="p-3 border border-slate-200 rounded-sm hover:border-soem-primary hover:bg-blue-50/30 cursor-pointer transition-all flex items-center justify-between"
                    onClick={() => handleLink(evt.id)} data-testid={`link-evt-${evt.id}`}>
                    <div>
                      <p className="text-xs text-slate-400 font-mono">{evt.event_code}</p>
                      <p className="font-semibold text-soem-navy text-sm">{et.emoji} {evt.name}</p>
                      {evt.location_name && <p className="text-xs text-slate-500 mt-0.5">📍 {evt.location_name}</p>}
                    </div>
                    <button className="soem-btn-primary px-3 py-1.5 text-xs flex items-center gap-1" disabled={linking}>
                      <Link size={12} /> Vincular
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="p-4 border-t bg-slate-50">
          <button onClick={onClose} className="soem-btn-secondary w-full py-2 text-sm">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isLider } = useAuth();

  const [project, setProject] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [events, setEvents] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [allowedTemplateIds, setAllowedTemplateIds] = useState([]);
  const [clientName, setClientName] = useState('');
  const [loading, setLoading] = useState(true);
  // #UnifyEvalCreation — createModal removido; las evaluaciones se
  // crean ahora desde el tab Evaluaciones del evento.
  const [createEventModal, setCreateEventModal] = useState(false);
  const [templatesModal, setTemplatesModal] = useState(false);
  const [editProjectModal, setEditProjectModal] = useState(false);
  const [linkEventModal, setLinkEventModal] = useState(false);
  // #ProjectTabsSimplify — antes habían 2 tabs (Evaluaciones + Eventos);
  // se quitó Evaluaciones porque se duplicaba con la lista global y la
  // pestaña Evaluaciones del evento. Ahora solo Eventos como tab por
  // defecto. Los contadores arriba (Evaluaciones/Publicadas/En Progreso/
  // Bloqueadas/Eventos) siguen mostrando los totales del proyecto.
  const [activeTab, setActiveTab] = useState('eventos');
  const [eventView, setEventView] = useState(() => localStorage.getItem('proj_evt_view') || 'cards');
  const [expandedEvt, setExpandedEvt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, evalsRes, eventsRes, tmplsRes, metaRes] = await Promise.all([
        projectsAPI.get(id),
        projectsAPI.getEvaluations(id).catch(() => ({ data: { items: [] } })),
        projectsAPI.getEvents(id).catch(() => ({ data: { items: [] } })),
        templatesAPI.list().catch(() => ({ data: { items: [] } })),
        projectMetaAPI.getAllowedTemplates(id).catch(() => ({ data: { templates: [] } })),
      ]);
      setProject(projRes.data);
      setEvaluations(evalsRes.data.items || []);
      setEvents(eventsRes.data.items || []);
      setAllTemplates(tmplsRes.data || []);
      setAllowedTemplateIds(metaRes.data.allowed_template_ids || []);

      if (projRes.data.client_id) {
        clientsAPI.list().then(r => {
          const c = (r.data.items || []).find(c => c.id === projRes.data.client_id);
          if (c) setClientName(c.name);
        }).catch(() => {});
      }

    } catch { toast.error('Error al cargar proyecto'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);


  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" />
    </div>
  );
  if (!project) return <div className="text-center py-12 text-slate-400">Proyecto no encontrado.</div>;

  const summary = buildSummary(evaluations);
  // Events helpers
  const eventNow = new Date();
  const eventsInCourse = events.filter(e => e.status === 'en_curso');
  const nextEvent = events.filter(e => e.event_start && new Date(e.event_start) > eventNow)
    .sort((a, b) => new Date(a.event_start) - new Date(b.event_start))[0];
  const eventStats = {
    total: events.length,
    planeacion: events.filter(e => e.status === 'planeacion').length,
    en_curso: events.filter(e => e.status === 'en_curso').length,
    finalizado: events.filter(e => ['finalizado', 'auditado'].includes(e.status)).length,
  };

  const setEvtView = (v) => { setEventView(v); localStorage.setItem('proj_evt_view', v); };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button aria-label="Volver a proyectos" onClick={() => navigate('/dashboard/proyectos')} className="soem-btn-secondary p-2 mt-1"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-soem-navy">{project.name}</h1>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${project.status === 'activo' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
              {project.status}
            </span>
            {summary.latestSemaforo && (
              <SemaforoBadge semaforo={summary.latestSemaforo} percentage={summary.latestPercentage} size="sm" />
            )}
          </div>
          <p className="text-slate-500 text-sm mt-0.5">{clientName || '—'} · {project.description || 'Sin descripción'}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {evaluations.filter(e => e.status === 'publicada').length >= 2 && (
            <button onClick={() => navigate(`/dashboard/proyectos/${id}/comparar`)}
              className="soem-btn-secondary px-3 py-2 text-xs flex items-center gap-1.5"
              data-testid="compare-btn">
              <GitCompare size={13} /> Comparar
            </button>
          )}
          {(isAdmin || isLider) && (
            <>
              <button onClick={() => setTemplatesModal(true)}
                className="soem-btn-secondary px-3 py-2 text-xs flex items-center gap-1.5">
                <Settings size={13} /> Plantillas
              </button>
              <button onClick={() => setEditProjectModal(true)}
                className="soem-btn-secondary px-3 py-2 text-xs flex items-center gap-1.5"
                data-testid="edit-project-btn">
                <Edit2 size={13} /> Editar
              </button>
            </>
          )}
        </div>
      </div>

      {/* Event in progress banner */}
      {eventsInCourse.length > 0 && (
        <div className="bg-green-50 border border-green-300 rounded-sm p-3 flex items-center gap-3 animate-pulse-once" data-testid="event-in-course-banner">
          <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse flex-shrink-0" />
          <span className="font-semibold text-green-800 text-sm">
            Evento en curso ahora: <strong>{eventsInCourse[0].name}</strong>
            {eventsInCourse[0].location_name && ` — ${eventsInCourse[0].location_name}`}
          </span>
          <button onClick={() => navigate(`/dashboard/events/${eventsInCourse[0].id}`)}
            className="ml-auto text-xs text-green-700 font-semibold hover:underline flex items-center gap-1">
            Ver evento <ArrowRight size={11} />
          </button>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {[
          { label: 'Evaluaciones', value: summary.total, icon: ClipboardList, color: '#1A56DB' },
          { label: 'Publicadas', value: summary.publishedCount, icon: CheckCircle, color: '#15803D' },
          { label: 'En progreso', value: summary.inProgressCount, icon: Clock, color: '#D97706' },
          { label: 'Bloqueadas', value: summary.blockedCount, icon: AlertTriangle, color: '#DC2626' },
          { label: 'Eventos', value: eventStats.total, icon: Calendar, color: '#7C3AED' },
          {
            label: 'Próximo evento', icon: Activity, color: '#0D9488',
            value: nextEvent ? new Date(nextEvent.event_start).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }) : '—',
            subtext: nextEvent ? nextEvent.name.slice(0, 20) : 'Sin eventos',
          },
        ].map(s => (
          <div key={s.label} className="soem-card p-4 border-t-4" style={{ borderTopColor: s.color }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="soem-label">{s.label}</p>
                <p className={`font-black text-soem-navy mt-0.5 ${typeof s.value === 'number' ? 'text-2xl' : 'text-base'}`}>{s.value}</p>
                {s.subtext && <p className="text-xs text-slate-400 truncate">{s.subtext}</p>}
              </div>
              <s.icon size={20} strokeWidth={1.5} style={{ color: s.color }} />
            </div>
          </div>
        ))}
      </div>

      {/* #ProjectTabsSimplify — layout reorganizado: antes era grid
          lg:cols-3 con score chart 1/3 izq + Eventos 2/3 der, dejando
          espacio desperdiciado bajo el chart. Ahora score chart +
          trend van EN PARALELO en la primera fila (cols-2), y Eventos
          ocupa una fila completa abajo (full-width). */}
      <div className="space-y-6">
        {/* Score panel arriba: latest published + trend chart side-by-side */}
        {(summary.latestEval || summary.scoreHistory.length >= 2) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Latest published */}
            {summary.latestEval && (
              <div className="soem-card p-5 border-t-4" style={{ borderTopColor:
                    evaluationPercentage(summary.latestEval) == null
                      ? '#94a3b8'
                      : summary.latestEval.semaforo === 'bajo' ? '#15803D'
                        : summary.latestEval.semaforo === 'medio' ? '#CA8A04'
                          : summary.latestEval.semaforo === 'alto' ? '#D97706' : '#DC2626',
                }}>
                <p className="soem-label mb-2">Última Publicada</p>
                <div className="text-center mb-3">
                  <SemaforoBadge semaforo={summary.latestEval.semaforo}
                    percentage={evaluationPercentage(summary.latestEval)} size="md" />
                  <p className="text-3xl font-black text-soem-navy mt-2">{summary.latestEval.score}</p>
                  <p className="text-slate-400 text-xs">v{summary.latestEval.version}</p>
                </div>
                {summary.improvementRate !== null && (
                  <div className={`flex items-center justify-center gap-1.5 text-sm font-bold mb-3 ${summary.improvementRate > 0 ? 'text-green-600' : summary.improvementRate < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                    {summary.improvementRate > 0 ? <TrendingUp size={16} /> : summary.improvementRate < 0 ? <TrendingDown size={16} /> : null}
                    <span>{summary.improvementRate > 0 ? '+' : ''}{summary.improvementRate}% vs anterior</span>
                  </div>
                )}
                <DictamenCard percentage={evaluationPercentage(summary.latestEval)} compact />
              </div>
            )}

            {/* Score history chart */}
            {summary.scoreHistory.length >= 2 && (
              <div className="soem-card p-4">
                <p className="font-bold text-soem-navy text-sm mb-2 flex items-center gap-2">
                  <BarChart2 size={14} className="text-soem-primary" /> Evolución del puntaje
                </p>
                <VersionTrendChart evaluations={evaluations} />
              </div>
            )}
          </div>
        )}

        {/* Eventos full-width — único tab */}
        <div className="space-y-4">
          <div className="flex gap-1 border-b border-slate-200">
            {[
              { id: 'eventos', label: `Eventos (${events.length})`, icon: Calendar },
            ].map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === t.id ? 'border-soem-primary text-soem-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                data-testid={`tab-${t.id}`}>
                <t.icon size={14} /> {t.label}
              </button>
            ))}
          </div>

          {activeTab === 'eventos' && (
            <div className="space-y-4" data-testid="events-panel">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex gap-3">
                  {[['Total', eventStats.total, '#7C3AED'], ['Planeación', eventStats.planeacion, '#1A56DB'], ['En Curso', eventStats.en_curso, '#15803D'], ['Finalizados', eventStats.finalizado, '#94a3b8']].map(([l, v, c]) => (
                    <div key={l} className="flex items-center gap-1.5 text-xs">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} />
                      <span className="text-slate-500">{l}:</span>
                      <span className="font-bold text-soem-navy">{v}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center border border-slate-300 rounded-sm overflow-hidden">
                    {[['cards', LayoutGrid], ['table', List], ['timeline', Activity]].map(([v, Icon]) => (
                      <button key={v} onClick={() => setEvtView(v)}
                        className={`p-1.5 ${eventView === v ? 'bg-soem-primary text-white' : 'text-slate-400 hover:bg-slate-50'}`}>
                        <Icon size={14} />
                      </button>
                    ))}
                  </div>
                  {(isAdmin || isLider) && (
                    <>
                      <button onClick={() => setLinkEventModal(true)} className="soem-btn-secondary px-3 py-1.5 text-xs flex items-center gap-1.5" data-testid="link-event-btn">
                        <Link size={13} /> Vincular Existente
                      </button>
                      <button onClick={() => setCreateEventModal(true)} className="soem-btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5" data-testid="create-event-btn">
                        <Plus size={13} /> Nuevo Evento
                      </button>
                    </>
                  )}
                </div>
              </div>

              {events.length === 0 ? (
                <div className="soem-card p-12 text-center">
                  <Calendar size={40} className="text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-500">No hay eventos vinculados a este proyecto.</p>
                  {(isAdmin || isLider) && <button onClick={() => setCreateEventModal(true)} className="soem-btn-primary px-5 py-2 text-sm mt-4 inline-flex items-center gap-2"><Plus size={14} /> Crear primer evento</button>}
                </div>
              ) : eventView === 'cards' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {events.map(evt => {
                    const et = EVENT_TYPES[evt.event_type] || { emoji: '📅', label: evt.event_type };
                    const stCfg = EVENT_STATUS[evt.status] || EVENT_STATUS.planeacion;
                    const rCfg = RISK_LEVEL[evt.projected_risk_level] || RISK_LEVEL.medio;
                    const dl = getDaysLabel(evt.event_start);
                    return (
                      <div key={evt.id} className="soem-card hover:shadow-md transition-shadow p-4 cursor-pointer" onClick={() => navigate(`/dashboard/events/${evt.id}`)} data-testid={`evt-card-${evt.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex gap-1.5">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${stCfg.bg}`}><span className={`w-1.5 h-1.5 rounded-full ${stCfg.dot}`} />{stCfg.label}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded font-bold border ${rCfg.bg}`}>{rCfg.label}</span>
                          </div>
                          <span className="text-slate-300 text-xs font-mono">{evt.event_code}</span>
                        </div>
                        <h3 className="font-bold text-soem-navy text-sm mb-0.5">{et.emoji} {evt.name}</h3>
                        {evt.location_name && <p className="text-xs text-slate-500 mb-2">📍 {evt.location_name}</p>}
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-400">{evt.event_start ? new Date(evt.event_start).toLocaleDateString('es-MX') : '—'}</span>
                          {dl && <span className={`font-semibold ${dl.color}`}>{dl.text}</span>}
                        </div>
                        <div className="flex gap-3 mt-2 text-xs text-slate-400 pt-2 border-t border-slate-100">
                          <EventTraceabilityStatus event={evt} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : eventView === 'table' ? (
                <div className="soem-card overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="soem-table-header">
                      <th className="px-4 py-3 text-left">Código</th>
                      <th className="px-4 py-3 text-left">Nombre</th>
                      <th className="px-4 py-3 text-left">Locación</th>
                      <th className="px-4 py-3 text-left">Fecha</th>
                      <th className="px-4 py-3 text-left">Días</th>
                      <th className="px-4 py-3 text-left">Riesgo</th>
                      <th className="px-4 py-3 text-left">Status</th>
                    </tr></thead>
                    <tbody>
                      {events.map(evt => {
                        const stCfg = EVENT_STATUS[evt.status] || EVENT_STATUS.planeacion;
                        const rCfg = RISK_LEVEL[evt.projected_risk_level] || RISK_LEVEL.medio;
                        const dl = getDaysLabel(evt.event_start);
                        return (
                          <tr key={evt.id} className="soem-table-row cursor-pointer" onClick={() => navigate(`/dashboard/events/${evt.id}`)}>
                            <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{evt.event_code}</td>
                            <td className="px-4 py-2.5 font-semibold text-soem-navy">{evt.name}</td>
                            <td className="px-4 py-2.5 text-xs text-slate-500">{evt.location_name || '—'}</td>
                            <td className="px-4 py-2.5 text-xs text-slate-500">{evt.event_start ? new Date(evt.event_start).toLocaleDateString('es-MX') : '—'}</td>
                            <td className={`px-4 py-2.5 text-xs font-semibold ${dl?.color || ''}`}>{dl?.text || '—'}</td>
                            <td className="px-4 py-2.5"><span className={`text-xs px-1.5 py-0.5 rounded font-bold border ${rCfg.bg}`}>{rCfg.label}</span></td>
                            <td className="px-4 py-2.5"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${stCfg.bg}`}><span className={`w-1.5 h-1.5 rounded-full ${stCfg.dot}`} />{stCfg.label}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="relative pl-8">
                  <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-slate-200" />
                  <div className="relative mb-4">
                    <div className="absolute -left-5 w-4 h-4 rounded-full bg-soem-navy border-2 border-white top-1" />
                    <div className="ml-2 border-b border-dashed border-slate-300 pb-2">
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">HOY</span>
                    </div>
                  </div>
                  {[...events].sort((a, b) => new Date(a.event_start || 0) - new Date(b.event_start || 0)).map(evt => {
                    const isPast = evt.event_start && new Date(evt.event_start) < eventNow;
                    const stCfg = EVENT_STATUS[evt.status] || EVENT_STATUS.planeacion;
                    const et = EVENT_TYPES[evt.event_type] || { emoji: '📅' };
                    const isExp = expandedEvt === evt.id;
                    const nc = evt.status === 'en_curso' ? '#15803D' : evt.status === 'planeacion' ? '#1A56DB' : evt.status === 'auditado' ? '#7C3AED' : '#94a3b8';
                    return (
                      <div key={evt.id} className={`relative mb-4 ${isPast ? 'opacity-60' : ''}`}>
                        <div className="absolute -left-5 top-2 w-4 h-4 rounded-full border-2 border-white" style={{ backgroundColor: nc }} />
                        <div className="soem-card p-3 hover:shadow-md cursor-pointer" onClick={() => setExpandedEvt(isExp ? null : evt.id)}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-bold text-soem-navy text-sm">{et.emoji} {evt.name}</p>
                              <p className="text-xs text-slate-400">{evt.event_start ? new Date(evt.event_start).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</p>
                            </div>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${stCfg.bg}`}><span className={`w-1.5 h-1.5 rounded-full ${stCfg.dot}`} />{stCfg.label}</span>
                          </div>
                          {isExp && (
                            <div className="mt-2 pt-2 border-t border-slate-100">
                              {evt.location_name && <p className="text-xs text-slate-500 mb-2">📍 {evt.location_name}</p>}
                              <button onClick={e => { e.stopPropagation(); navigate(`/dashboard/events/${evt.id}`); }} className="soem-btn-primary px-3 py-1.5 text-xs w-full">Ver detalle completo</button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* #UnifyEvalCreation — CreateEvalModal removido. La creación de
          evaluaciones ahora vive sólo en el tab Evaluaciones de cada
          evento (EvaluationsTab.jsx). */}

      {createEventModal && (
        <CreateProjectEventModal project={project}
          onCreated={(evt) => { setEvents(prev => [...prev, evt]); setCreateEventModal(false); }}
          onClose={() => setCreateEventModal(false)} />
      )}

      {templatesModal && (
        <AllowedTemplatesModal projectId={id} allTemplates={allTemplates} current={allowedTemplateIds}
          onSave={setAllowedTemplateIds} onClose={() => setTemplatesModal(false)} />
      )}

      {editProjectModal && (
        <EditProjectModal 
          project={project} 
          onSave={async (form) => {
            await projectsAPI.update(id, form);
            toast.success('Proyecto actualizado');
            load();
          }}
          onClose={() => setEditProjectModal(false)} 
        />
      )}

      {linkEventModal && (
        <LinkEventModal 
          projectId={id}
          existingEventIds={events.map(e => e.id)}
          onLinked={load}
          onClose={() => setLinkEventModal(false)} 
        />
      )}
    </div>
  );
}
